import { ColorPalette } from "../background";
import {
  OptimizelyContentArea,
  OptimizelyContentLink,
  OptimizelyPropertyBoolean,
} from "./base";
import {
  OptimizelyContentReferenceExpandedValue,
  OptimizelyExpandedValue,
} from "./expanded";
import { PropertyDataType } from "./unions";

export interface OptimizelyBlock {
  value?: string | Object | Object[];
  propertyDataType: PropertyDataType;
  contentLink?: {
    guidValue: string;
  };
}

export interface OptimizelyLongString extends OptimizelyBlock {
  value: string;
  propertyDataType: "PropertyLongString";
}

export interface OptimizelyNumber extends OptimizelyBlock {
  value: number;
  propertyDataType: "PropertyNumber";
}

export interface OptimizelyXhtmlString extends OptimizelyBlock {
  value: string;
  propertyDataType: "PropertyXhtmlString";
}
export interface OptimizelyColor {
  value: ColorPalette | null;
  propertyDataType: "PropertyLongString";
}

export interface OptimizelyURL extends OptimizelyBlock {
  value: string;
  propertyDataType: "PropertyUrl";
}

export interface OptimizelyLinkCollection {
  propertyDataType: "PropertyLinkCollection";
  value: {
    href: string;
    text: string;
    contentLink?: OptimizelyContentLink;
  }[];
  expandedValue?: OptimizelyExpandedValue[];
}

export interface OptimizelyContentReference extends OptimizelyBlock {
  propertyDataType: "PropertyContentReference";
  expandedValue?: OptimizelyContentReferenceExpandedValue;
  value?: {
    id: number;
    workId: number;
    guidValue: string;
    url: string;
  };
}

export interface OptimizelyContentReferenceList extends OptimizelyBlock {
  value: Object[];
  propertyDataType: "PropertyContentReferenceList";
  expandedValue: OptimizelyContentReferenceExpandedValue[];
}

export interface TeasedNewsPage extends OptimizelyExpandedValue {
  contentType: ["Page", "NewsPage"] | ["Page", "PressReleasePage"];
  url: string;
  name: string;
  mainIntro: OptimizelyLongString;
  mainImage?: OptimizelyContentReference;
  listImage?: OptimizelyContentReference;
  teaserBackgroundColor?: OptimizelyColor;
  teaserHideImage: OptimizelyPropertyBoolean;
  organization: OptimizelyPropertyCategory;
}

export const isTeasedNewsPage = (
  block: OptimizelyExpandedValue
): block is TeasedNewsPage => {
  return (
    block.contentType[1] === "NewsPage" ||
    block.contentType[1] === "PressReleasePage"
  );
};

export interface ArticlePageIndustryPerpectives
  extends OptimizelyExpandedValue {
  contentType: ["Page", "ArticlePageIndustryPerpectives"];
  url: string;
  name: string;
  mainIntro: OptimizelyLongString;
  mainImage?: OptimizelyContentReference;
  listImage?: OptimizelyContentReference;
  teaserBackgroundColor?: OptimizelyColor;
  teaserHideImage: OptimizelyPropertyBoolean;
  organization: OptimizelyPropertyCategory;
}

export const isArticlePageIndustryPerpectives = (
  block: OptimizelyExpandedValue
): block is ArticlePageIndustryPerpectives => {
  return block.contentType[1] === "ArticlePageIndustryPerpectives";
};

export interface SectionPage extends OptimizelyExpandedValue {
  contentType: ["Page", "SectionPage"];
  url: string;
  name: string;
  mainIntro: OptimizelyLongString;
  mainImage?: OptimizelyContentReference;
  listImage?: OptimizelyContentReference;
  teaserBackgroundColor?: OptimizelyColor;
  teaserHideImage: OptimizelyPropertyBoolean;
  organization: OptimizelyPropertyCategory;
}

export const isSectionPage = (
  block: OptimizelyExpandedValue
): block is SectionPage => {
  return block.contentType[1] === "SectionPage";
};

export const isCenteredSectionPage = (
  block: OptimizelyExpandedValue
): block is SectionPage => {
  return block.contentType[1] === "SectionCenteredPage";
};

export const isSolutionPage = (
  block: OptimizelyExpandedValue
): block is SectionPage => {
  return block.contentType[1] === "SolutionPage";
};

export interface SimpleEventPage extends OptimizelyExpandedValue {
  contentType: ["Page", "SimpleEventPage"];
  url: string;
  name: string;
  mainIntro: OptimizelyLongString;
  mainImage?: OptimizelyContentReference;
  listImage?: OptimizelyContentReference;
  teaserBackgroundColor?: OptimizelyColor;
  teaserHideImage: OptimizelyPropertyBoolean;
  organization: OptimizelyPropertyCategory;
}

export const isSimpleEventPage = (
  block: OptimizelyExpandedValue
): block is SimpleEventPage => {
  return block.contentType[1] === "SimpleEventPage";
};

export interface CoursePage extends OptimizelyExpandedValue {
  contentType: ["Page", "CoursePage"];
  url: string;
  name: string;
  mainIntro: OptimizelyLongString;
  mainImage?: OptimizelyContentReference;
  listImage?: OptimizelyContentReference;
  teaserBackgroundColor?: OptimizelyColor;
  teaserHideImage: OptimizelyPropertyBoolean;
  organization: OptimizelyPropertyCategory;
}

export const isCoursePage = (
  block: OptimizelyExpandedValue
): block is CoursePage => {
  return block.contentType[1] === "CoursePage";
};

export interface PortNewsPage extends OptimizelyExpandedValue {
  contentType: ["Page", "PortNewsPage"];
  url: string;
  name: string;
  mainIntro: OptimizelyLongString;
  mainImage?: OptimizelyContentReference;
  listImage?: OptimizelyContentReference;
  teaserBackgroundColor?: OptimizelyColor;
  teaserHideImage: OptimizelyPropertyBoolean;
  organization: OptimizelyPropertyCategory;
}

export const isPortNewsPage = (
  block: OptimizelyExpandedValue
): block is PortNewsPage => {
  return block.contentType[1] === "PortNewsPage";
};

export interface StatsNumbersBlockValues extends OptimizelyExpandedValue {
  title: OptimizelyLongString;
  buttonTitle: OptimizelyLongString;
  buttonLink: OptimizelyLongString;
  sectionId?: OptimizelyLongString;
  statsNumbersContentArea: {
    expandedValue: StatsNumbersContentBlock[];
    propertyDataType: "PropertyContentArea";
  };
}

export interface StatsNumbersContentBlock extends OptimizelyExpandedValue {
  contentType: ["Block", "StatsNumbersContentBlock"];
  number: {
    value: string;
    propertyDataType: "PropertyLongString";
  };
  title: {
    value: string;
    propertyDataType: "PropertyLongString";
  };
  introduction: {
    value: string;
    propertyDataType: "PropertyLongString";
  };
}

export interface ImageReference extends OptimizelyContentReference {
  value: {
    guidValue: string;
    id: number;
    url: string;
    workId: number;
  };
}

export interface CTASection extends OptimizelyExpandedValue {
  propertyDataType: "CTALeft" | "CTARight";
  buttonLink: OptimizelyURL;
  description: OptimizelyLongString;
  heading: OptimizelyLongString;
  image: ImageReference;
  introduction: OptimizelyLongString;
  linkText: OptimizelyLongString;
  name: "CTALeft" | "CTARight";
  backgroundColor: OptimizelyColor;
}

export interface Image extends OptimizelyExpandedValue {
  contentType: ["Image", "Media"];
  url: string;
  title: OptimizelyLongString;
}

export interface TeaserListBlock extends OptimizelyExpandedValue {
  contentType: ["Block", "TeaserListBlock"];
  content: OptimizelyContentArea;
  headerLink: OptimizelyPropertyUrl;
  headerLinkText: OptimizelyLongString;
  navigateToArticleText: OptimizelyLongString;
  sectionId?: OptimizelyLongString;
  name: string;
}

export interface StatsNumbersBlock
  extends OptimizelyExpandedValue,
    StatsNumbersBlockValues {
  contentType: ["Block", "StatsNumbersBlock"];
}

export const isTeaserListBlock = (
  block: OptimizelyExpandedValue
): block is TeaserListBlock => {
  return block.contentType[1] === "TeaserListBlock";
};

export interface CTADoubleBlock extends OptimizelyExpandedValue {
  contentType: ["Block", "CallToActionDoubleBlock"];
  ctaLeft: CTASection;
  ctaRight: CTASection;
  sectionId?: OptimizelyLongString;
}

export const isCTADoubleBlock = (
  block: OptimizelyExpandedValue
): block is CTADoubleBlock => {
  return block.contentType[1] === "CallToActionDoubleBlock";
};

export interface TextAndImageBlock extends OptimizelyExpandedValue {
  contentType: ["Block", "TextAndImageBlock"];
  image: {
    expandedValue: OptimizelyContentReferenceExpandedValue;
  };
  title?: OptimizelyLongString;
  description: OptimizelyXhtmlString;
  backgroundColor?: OptimizelyColor;
}

export const isTextAndImageBlock = (
  block: OptimizelyExpandedValue
): block is TextAndImageBlock => {
  return block.contentType[1] === "TextAndImageBlock";
};

export interface TextBlock extends OptimizelyExpandedValue {
  contentType: ["Block", "TextBlock"];
  text: OptimizelyXhtmlString;
  backgroundColor: OptimizelyColor;
  isLogoVisible: OptimizelyPropertyBoolean;
}

export const isTextBlock = (
  block: OptimizelyExpandedValue
): block is TextBlock => {
  return block.contentType[1] === "TextBlock";
};

export interface EditorialBlock extends OptimizelyExpandedValue {
  contentType: ["Block", "EditorialBlock"];
  text: OptimizelyXhtmlString;
  title: OptimizelyLongString;
  backgroundColor: OptimizelyColor;
  sectionId?: OptimizelyLongString;
}

export const isEditorialBlock = (
  block: OptimizelyExpandedValue
): block is EditorialBlock => {
  return block.contentType[1] === "EditorialBlock";
};

export interface CTABlock extends OptimizelyExpandedValue {
  contentType: ["Block", "CallToActionBlock"];
  linkText: OptimizelyLongString;
  buttonLink: OptimizelyURL;
  heading: OptimizelyLongString;
  introduction: OptimizelyLongString;
}

export const isCTABlock = (
  block: OptimizelyExpandedValue
): block is CTABlock => {
  return block.contentType[1] === "CallToActionBlock";
};

export interface ContactBlock extends OptimizelyExpandedValue {
  contentType: ["Block", "ContactBlock"];
  title: OptimizelyLongString;
  text: OptimizelyLongString;
  link: OptimizelyURL;
}

export const isContactBlock = (
  block: OptimizelyExpandedValue
): block is ContactBlock => {
  return block.contentType[1] === "ContactBlock";
};

export interface SpacerBlock extends OptimizelyExpandedValue {
  contentType: ["Block", "SpacerBlock"];
  height: OptimizelyNumber;
  backgroundColor: OptimizelyColor;
  hideOnMobile: OptimizelyPropertyBoolean;
}

export const isSpacerBlock = (
  block: OptimizelyExpandedValue
): block is SpacerBlock => {
  return block.contentType[1] === "SpacerBlock";
};

export interface TwoColumnsBlock extends OptimizelyExpandedValue {
  contentType: ["Block", "TwoColumnsBlock"];
  leftColumn: OptimizelyContentArea;
  rightColumn: OptimizelyContentArea;
  leftColumnScrollableContent: OptimizelyPropertyBoolean;
  rightColumnScrollableContent: OptimizelyPropertyBoolean;
}

export const isTwoColumnsBlock = (
  block: OptimizelyExpandedValue
): block is TwoColumnsBlock => {
  return block.contentType[1] === "TwoColumnsBlock";
};

export interface AccordionBlock extends OptimizelyExpandedValue {
  contentType: ["Block", "AccordionBlock"];
  accordionItems: OptimizelyContentArea;
}

export const isAccordionBlock = (
  block: OptimizelyExpandedValue
): block is AccordionBlock => {
  return block.contentType[1] === "AccordionBlock";
};

export const isStatsNumbersBlock = (
  block: OptimizelyExpandedValue
): block is StatsNumbersBlock => {
  return block.contentType[1] === "StatsNumbersBlock";
};

export interface LinkListBlock extends OptimizelyExpandedValue {
  contentType: ["Block", "LinkListBlock"];
  links: OptimizelyLinkCollection;
  title: OptimizelyLongString;
  name: string;
}

export const isLinkListBlock = (
  block: OptimizelyExpandedValue
): block is LinkListBlock => {
  return block.contentType[1] === "LinkListBlock";
};

export interface GalleryBlock extends OptimizelyExpandedValue {
  contentType: ["Block", "TeaserGalleryBlock"];
}

export type MainContentAreaExpandedValue =
  | TeaserListBlock
  | GalleryBlock
  | ProductsBlock;

interface CatalogedProductPage extends OptimizelyExpandedValue {
  contentType: ["Page", "CatalogedProductPage"];
  name: string;
  description: OptimizelyLongString;
  images: OptimizelyLinkCollection;
  number: OptimizelyLongString;
  url: string;
}

export interface ProductsBlock extends OptimizelyExpandedValue {
  contentType: ["Block", "CatalogueProductsPreviewBlock"];
  products: {
    expandedValue: CatalogedProductPage[];
    propertyDataType: "PropertyContentReferenceList";
    value: {
      guidValue: string;
      id: number;
      url: string;
      workId: number;
    }[];
  };
  title: OptimizelyLongString;
  sectionId?: OptimizelyLongString;
}

export const isProductsBlock = (
  block: OptimizelyExpandedValue
): block is ProductsBlock => {
  return block.contentType[1] === "CatalogueProductsPreviewBlock";
};

export type OptimizelyButtonTypeValue =
  | "main"
  | "secondary"
  | "link-primary"
  | "link-secondary"
  | "text"
  | "main-reverse"
  | "link-primary-reverse"
  | "link-secondary-reverse";

export interface OptimizelyButtonType {
  value: OptimizelyButtonTypeValue;
  propertyDataType: string;
}

export interface OptimizelyPropertyUrl {
  value: string;
  propertyDataType: "PropertyUrl";
}

export interface OptimizelyCallToActionButtonBlock
  extends OptimizelyExpandedValue {
  contentType: ["Block", "CallToActionButtonBlock"];
  linkText?: OptimizelyLongString;
  buttonLink: OptimizelyURL;
  buttonType: OptimizelyButtonType;
}

export interface OptimizelyCallToActionButtonsContentArea {
  expandedValue: OptimizelyCallToActionButtonBlock[];
  propertyDataType: "PropertyContentArea";
}

export interface OptimizelyFullImageBlock extends OptimizelyExpandedValue {
  contentType: ["Block", "FullImageBlock"];
  headerText: OptimizelyLongString;
  title: OptimizelyLongString;
  subTitle: OptimizelyLongString;
  image: OptimizelyContentReference;
  imageMobile: OptimizelyContentReference;
  callToActionButtons: OptimizelyCallToActionButtonsContentArea;
  headerCallToActionButtons: OptimizelyCallToActionButtonsContentArea;
  fontColor: OptimizelyColor;
  backgroundColor: OptimizelyColor;
  fullHeight: OptimizelyPropertyBoolean;
  imageCaption: OptimizelyLongString;
  sectionId: OptimizelyLongString;
}

export const isFullImageBlock = (
  block: OptimizelyExpandedValue
): block is OptimizelyFullImageBlock => {
  return block.contentType[1] === "FullImageBlock";
};

export interface OptimizelyPropertyCategoryValue {
  id: number;
  name: string;
  description: string;
}

export interface OptimizelyPropertyCategory {
  value: OptimizelyPropertyCategoryValue[];
  propertyDataType: "PropertyCategory";
}

export interface OptimizelyArticlePage extends OptimizelyExpandedValue {
  contentType: ["Page", "ArticlePage"];
  contentLink: OptimizelyContentLink;
  mainIntro: OptimizelyLongString;
  listImage?: OptimizelyContentReference;
  organization: OptimizelyPropertyCategory;
  url: string;
  name: string;
  mainImage?: OptimizelyContentReference;
  teaserBackgroundColor?: OptimizelyColor;
  teaserHideImage: OptimizelyPropertyBoolean;
}

export const isArticlePage = (
  block: OptimizelyExpandedValue
): block is OptimizelyArticlePage => {
  return block.contentType[1] === "ArticlePage";
};

export interface OptimizelyPressReleasePage extends OptimizelyExpandedValue {
  contentType: ["Page", "PressReleasePage"];
  url: string;
  name: string;
  mainIntro: OptimizelyLongString;
  mainImage?: OptimizelyContentReference;
  listImage?: OptimizelyContentReference;
  teaserBackgroundColor?: OptimizelyColor;
  teaserHideImage: OptimizelyPropertyBoolean;
  organization: OptimizelyPropertyCategory;
}

export const isPressReleasePage = (
  block: OptimizelyExpandedValue
): block is OptimizelyPressReleasePage => {
  return block.contentType[1] === "PressReleasePage";
};

export interface OptimizelyNewsPage extends OptimizelyExpandedValue {
  contentType: ["Page", "NewsPage"];
  url: string;
  name: string;
  mainIntro: OptimizelyLongString;
  mainImage?: OptimizelyContentReference;
}

export const isNewsPage = (
  block: OptimizelyExpandedValue
): block is OptimizelyNewsPage => {
  return block.contentType[1] === "NewsPage";
};

export interface OptimizelyEmployeeTeaserListBlockContentArea {
  expandedValue: OptimizelyArticlePage[];
  propertyDataType: "PropertyContentArea";
}

export interface OptimizelyEmployeeTeaserListBlock
  extends OptimizelyExpandedValue {
  contentType: ["Block", "EmployeeTeaserListBlock"];
  title: OptimizelyLongString;
  boldTitle: OptimizelyLongString;
  secondaryTitle: OptimizelyLongString;
  headerType: OptimizelyNumber;
  callToActionButton: OptimizelyCallToActionButtonBlock;
  content: OptimizelyEmployeeTeaserListBlockContentArea;
  sectionId?: OptimizelyLongString;
}

export const isEmployeeTeaserListBlock = (
  block: OptimizelyExpandedValue
): block is OptimizelyEmployeeTeaserListBlock => {
  return block.contentType[1] === "EmployeeTeaserListBlock";
};

export interface OptimizelySocialMediaWallBlock
  extends OptimizelyExpandedValue {
  contentType: ["Block", "SocialMediaWallBlock"];
  juicerFeedId: OptimizelyLongString;
  numberOfItems: OptimizelyNumber;
  heading: OptimizelyLongString;
}
export const isSocialMediaWallBlock = (
  block: OptimizelyExpandedValue
): block is OptimizelySocialMediaWallBlock => {
  return block.contentType[1] === "SocialMediaWallBlock";
};


export interface ColumnsBlockItem extends OptimizelyExpandedValue {
  contentType: ["Block", "ColumnsBlockItem"];
  title: OptimizelyLongString;
  subtitle: OptimizelyXhtmlString;
  linkList: OptimizelyLinkCollection;
  iconImage: OptimizelyContentReference;
  itemImage: OptimizelyContentReference;
  backgroundColor: OptimizelyColor;
  borderColor: OptimizelyColor;
}

export interface ColumnsBlock extends OptimizelyExpandedValue {
  title: OptimizelyLongString;
  boldTitle: OptimizelyLongString;
  headerType: OptimizelyNumber;
  contentType: ["Block", "ColumnsBlock"];
  items: {
    expandedValue: ColumnsBlockItem[];
    propertyDataType: "PropertyContentArea";
  };
  columnAlignment: OptimizelyLongString;
  hideTopSpacing: OptimizelyPropertyBoolean;
  hideBottomSpacing: OptimizelyPropertyBoolean;
  sectionId?: OptimizelyLongString;
}

export const isColumnsBlock = (
  block: OptimizelyExpandedValue
): block is ColumnsBlock => {
  return block.contentType[1] === "ColumnsBlock";
};

export interface  PagePreviewBlock extends OptimizelyExpandedValue {
  contentType: ["Block", "PreviewBlock"];
  title: OptimizelyLongString;
  descriptionText: OptimizelyXhtmlString;
  image: OptimizelyContentReference;
  backgroundColor: OptimizelyColor;
  readMoreLink: OptimizelyURL;
  readMoreLinkText: OptimizelyLongString;
  headerType?: OptimizelyNumber;
  firstColumnArticle?: OptimizelyContentReference;
  secondColumnArticle?: OptimizelyContentReference;
  thirdColumnArticle?: OptimizelyContentReference;
  fourthColumnArticle?: OptimizelyContentReference;
  firstColumnLinkItemCollection?: OptimizelyLinkCollection;
  secondColumnLinkItemCollection?: OptimizelyLinkCollection;
  thirdColumnLinkItemCollection?: OptimizelyLinkCollection;
  fourthColumnLinkItemCollection?: OptimizelyLinkCollection;
  renderType?: OptimizelyNumber;
  showBackground?: OptimizelyPropertyBoolean;
  industryPerspectiveMode?: OptimizelyPropertyBoolean;
  sectionId?: OptimizelyLongString;
}

export const isPagePreviewBlock = (
  block: OptimizelyExpandedValue
): block is PagePreviewBlock => {
  return block.contentType[1] === "PreviewBlock";
};

export interface AnchorBlock extends OptimizelyExpandedValue {
  contentType: ["Block", "AnchorBlock"];
  enabled: OptimizelyPropertyBoolean;
  id: OptimizelyLongString;
}

export const isAnchorBlock = (
  block: OptimizelyExpandedValue
): block is AnchorBlock => {
  return block.contentType[1] === "AnchorBlock";
};

export interface IFrameBlock extends OptimizelyExpandedValue {
  contentType: ["Block", "IFrameBlock"];
  iFrameUrl: OptimizelyURL;
  iframeType: OptimizelyNumber;
  height: OptimizelyLongString;
  fullWidth: OptimizelyPropertyBoolean;
  isPreferences: OptimizelyPropertyBoolean;
  isStatistics: OptimizelyPropertyBoolean;
  isMarketing: OptimizelyPropertyBoolean;
}

export const isIFrameBlock = (
  block: OptimizelyExpandedValue
): block is IFrameBlock => {
  return block.contentType[1] === "IFrameBlock";
};